import type { TerritorySnapshot } from '@dreamgame/shared';
import { computed, signal } from '@preact/signals';

// ============================================================================
// Territory State - Manages client-side territory data
// ============================================================================

export interface TerritoryData extends TerritorySnapshot {
  readonly ownerName?: string;
}

// All known territories (keyed by networkId)
export const territories = signal<Map<number, TerritoryData>>(new Map());

// Currently selected territory (by flag networkId)
export const selectedTerritoryId = signal<number | null>(null);

// Derived state
export const selectedTerritory = computed((): TerritoryData | null => {
  const id = selectedTerritoryId.value;
  if (id === null) {
    return null;
  }
  return territories.value.get(id) ?? null;
});

export const hasTerritorySelection = computed(() => selectedTerritoryId.value !== null);

// Get territory by networkId
export const getTerritory = ({ networkId }: { networkId: number }): TerritoryData | null => {
  return territories.value.get(networkId) ?? null;
};

// Get territory at a tile position
export const getTerritoryAtTile = ({
  tileX,
  tileY,
}: {
  tileX: number;
  tileY: number;
}): TerritoryData | null => {
  for (const territory of territories.value.values()) {
    const dx = Math.abs(tileX - territory.centerX);
    const dy = Math.abs(tileY - territory.centerY);
    if (dx <= territory.radius && dy <= territory.radius) {
      return territory;
    }
  }
  return null;
};

// Get territory by flag position (exact match)
export const getTerritoryByFlagPosition = ({
  tileX,
  tileY,
}: {
  tileX: number;
  tileY: number;
}): TerritoryData | null => {
  for (const territory of territories.value.values()) {
    if (territory.centerX === tileX && territory.centerY === tileY) {
      return territory;
    }
  }
  return null;
};

// Actions
export const setTerritory = ({ territory }: { territory: TerritoryData }): void => {
  const newMap = new Map(territories.value);
  newMap.set(territory.networkId, territory);
  territories.value = newMap;
};

export const setTerritories = ({ items }: { items: TerritoryData[] }): void => {
  const newMap = new Map(territories.value);
  for (const territory of items) {
    newMap.set(territory.networkId, territory);
  }
  territories.value = newMap;
};

export const removeTerritory = ({ networkId }: { networkId: number }): void => {
  const newMap = new Map(territories.value);
  newMap.delete(networkId);
  territories.value = newMap;

  // Clear selection if removed territory was selected
  if (selectedTerritoryId.value === networkId) {
    selectedTerritoryId.value = null;
  }
};

export const selectTerritory = ({ networkId }: { networkId: number }): void => {
  selectedTerritoryId.value = networkId;
};

export const deselectTerritory = (): void => {
  selectedTerritoryId.value = null;
};

export const clearTerritories = (): void => {
  territories.value = new Map();
  selectedTerritoryId.value = null;
};

// Get all territories for a specific owner
export const getTerritoriesByOwner = ({ ownerId }: { ownerId: number }): TerritoryData[] => {
  return Array.from(territories.value.values()).filter((t) => t.ownerId === ownerId);
};

// Check if a tile is in any territory
export const isTileInTerritory = ({ tileX, tileY }: { tileX: number; tileY: number }): boolean => {
  return getTerritoryAtTile({ tileX, tileY }) !== null;
};
